import { useEffect } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  title: string;
  subtitle?: string;
  action?: ToastAction;
  onDismiss: () => void;
  /** ms before auto-dismiss; 0 disables. */
  durationMs?: number;
}

/**
 * Confirmation toast (Component - Toast and Notifications.dc.html): dark inverse surface,
 * accent-colored action. Self-contained to this module — no shared toast system exists yet
 * (chrome only has the PWA update toast), and this one is scoped to recipe actions.
 */
export function Toast({
  title,
  subtitle,
  action,
  onDismiss,
  durationMs = 6000,
}: ToastProps): JSX.Element {
  useEffect(() => {
    if (durationMs <= 0) return;
    const timer = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(timer);
  }, [durationMs, onDismiss]);

  return (
    <div
      role="status"
      data-testid="recipe-toast"
      className="fixed inset-x-4 bottom-[calc(72px+env(safe-area-inset-bottom))] z-30 mx-auto flex max-w-sm items-center gap-3 rounded-lg bg-inverse px-4 py-3 text-inverse-fg shadow-lg ease-app lg:bottom-4 lg:left-4 lg:right-auto"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{title}</div>
        {subtitle && <div className="truncate text-xs opacity-70">{subtitle}</div>}
      </div>
      {action && (
        <button
          type="button"
          data-testid="recipe-toast-action"
          onClick={() => {
            action.onClick();
            onDismiss();
          }}
          className="shrink-0 rounded-md px-2 py-1.5 text-sm font-bold text-accent"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
